import { OmitStrict, replaceTokensWithValues, splitTextWithTokens, TokensWithNodes } from "@amx/ux-twoui-utils";
import React from "react";
import { useComponentLogger } from "../../shared/logging";
import { Hyperlink } from "../Links/Hyperlink/Hyperlink";
import { NoHrefLink } from "../Links/NoHrefLink/NoHrefLink";
import { Text, TextProps } from "../Text/Text";
import { TruncatedText, TruncatedTextTextProps } from "../TruncatedText/TruncatedText";

export interface RichTextProps extends TextPropsWithoutChildren {
    /**
     * String with tokens surrounded by curly braces.
     * Example: "Click {link} to see more information"
     */
    readonly textWithTokens: string;
    /**
     * Object with tokens as keys and JSX Elements or strings as values to replace the tokens in the `textWithTokens`
     * Example: { "link": <NoHrefLink onClick={showMoreInfo} data-bi-id="bi-id" aria-label="aria-label">here</NoHrefLink> }
     */
    readonly tokensAndValues: TokensWithNodes;

    /** If specified, renders `TruncatedText` internally */
    readonly truncatedTextProps?: OmitStrict<TruncatedTextTextProps, "children">;
}

type TextPropsWithoutChildren = OmitStrict<TextProps, "children">;

export const RichText: React.FC<RichTextProps> = ({
    textWithTokens,
    tokensAndValues,
    truncatedTextProps,
    ...textProps
}) => {
    useComponentLogger("RichText", textProps.id);

    // Splits textWithTokens on each token
    const textSplitWithTokens = splitTextWithTokens(textWithTokens);

    return truncatedTextProps ? (
        <TruncatedText
            {...textProps}
            {...truncatedTextProps}
        >
            {replaceTokensWithValues(textSplitWithTokens, tokensAndValues, underlineLink)}
        </TruncatedText>
    ) : (
        <Text {...textProps}>{replaceTokensWithValues(textSplitWithTokens, tokensAndValues, underlineLink)}</Text>
    );
};

const underlineLink = (reactNodeValue: JSX.Element): JSX.Element => {

    const noHrefLinkComponent: readonly React.ComponentType<any>[] = [
        NoHrefLink
    ];
    const hyperlinkComponent: readonly React.ComponentType<any>[] = [
        Hyperlink
    ];

    const elementType = reactNodeValue?.type;
    //test if the node is type NoHrefLink or Hyperlink
    if (noHrefLinkComponent.includes(elementType)) {
        const props = reactNodeValue["props"];
        return <NoHrefLink {...props} underline={true} />;
    }
    if (hyperlinkComponent.includes(elementType)) {
        const props = reactNodeValue["props"];
        return <Hyperlink {...props} underline={true} />;
    }

    return reactNodeValue;
};
